import { useState, useEffect } from 'react';
import { FileText, Plus, Trash2, Eye, Printer, Upload, X, File, Calendar } from 'lucide-react';
import API_URL from '../config/api';

const PatientConsentsHistory = ({ patientId }) => {
    const [consents, setConsents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form state
    const [description, setDescription] = useState('');
    const [pdfFile, setPdfFile] = useState(null);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (patientId) {
            fetchConsents();
        }
    }, [patientId]);

    const fetchConsents = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/patients/${patientId}/consents`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setConsents(data);
            }
        } catch (err) {
            console.error('Error fetching consents:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = () => {
        setDescription('');
        setPdfFile(null);
        setNotes('');
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!description || !pdfFile) {
            alert('Por favor complete la descripción y seleccione un archivo PDF.');
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('description', description);
            formData.append('pdf_file', pdfFile);
            if (notes) formData.append('notes', notes);

            const response = await fetch(`${API_URL}/patients/${patientId}/consents`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (response.ok) {
                setShowModal(false);
                fetchConsents();
            } else {
                const error = await response.json();
                alert(error.message || 'Error al guardar el consentimiento');
            }
        } catch (err) {
            console.error('Error saving consent:', err);
            alert('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Está seguro de eliminar este consentimiento? Esta acción no se puede deshacer.')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/patients/${patientId}/consents/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                fetchConsents();
            } else {
                alert('Error al eliminar el consentimiento');
            }
        } catch (err) {
            console.error('Error deleting consent:', err);
            alert('Error de conexión');
        }
    };

    const handlePrintPdf = (pdfPath) => {
        if (!pdfPath) {
            alert('No hay archivo PDF disponible');
            return;
        }
        const pdfUrl = pdfPath.startsWith('http') ? pdfPath : `${API_URL}/${pdfPath}`;
        const printWindow = window.open(pdfUrl, '_blank');
        if (printWindow) {
            printWindow.addEventListener('load', () => {
                printWindow.print();
            });
        }
    };

    const handleViewPdf = (pdfPath) => {
        if (!pdfPath) {
            alert('No hay archivo PDF disponible');
            return;
        }
        const pdfUrl = pdfPath.startsWith('http') ? pdfPath : `${API_URL}/${pdfPath}`;
        window.open(pdfUrl, '_blank');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading && consents.length === 0) {
        return <div className="p-4 text-center text-gray-500">Cargando consentimientos...</div>;
    }

    return (
        <div className="patient-consents-history">
            <div className="consents-header">
                <div className="header-title">
                    <FileText size={24} />
                    <h3>Expediente de Consentimientos</h3>
                </div>
                <button className="btn-primary" onClick={handleOpenModal}>
                    <Plus size={18} />
                    Subir Nuevo
                </button>
            </div>

            <div className="consents-list">
                {consents.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <p>No hay consentimientos registrados en el expediente</p>
                        <button onClick={handleOpenModal}>Subir el primero</button>
                    </div>
                ) : (
                    <div className="consents-table">
                        {consents.map(consent => (
                            <div key={consent.id} className="consent-row">
                                <div className="consent-info">
                                    <div className="consent-name">
                                        <FileText size={18} className="icon-doc" />
                                        {/* Fallback to template name or notes if available */}
                                        <span>{consent.template_name || consent.notes || 'Consentimiento sin nombre'}</span>
                                    </div>
                                    <div className="consent-meta">
                                        <span className="date">
                                            <Calendar size={14} />
                                            {formatDate(consent.signed_at)}
                                        </span>
                                        {consent.notes && consent.notes !== consent.template_name && (
                                            <span className="notes-preview">
                                                {consent.notes.length > 50 ? consent.notes.substring(0, 50) + '...' : consent.notes}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="consent-actions">
                                    {consent.pdf_path && (
                                        <>
                                            <button
                                                className="btn-icon view"
                                                title="Ver PDF"
                                                onClick={() => handleViewPdf(consent.pdf_path)}
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                className="btn-icon print"
                                                title="Imprimir PDF"
                                                onClick={() => handlePrintPdf(consent.pdf_path)}
                                            >
                                                <Printer size={16} />
                                            </button>
                                        </>
                                    )}
                                    <button
                                        className="btn-icon delete"
                                        title="Eliminar"
                                        onClick={() => handleDelete(consent.id)}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal de Subida */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content consent-upload-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Subir Consentimiento Firmado</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>Nombre / Descripción *</label>
                                    <input
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Ej: Consentimiento Informado General"
                                        required
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Notas Adicionales (Opcional)</label>
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Detalles adicionales..."
                                        className="form-textarea"
                                        rows="2"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Archivo PDF *</label>
                                    <div className="pdf-upload-area">
                                        <input
                                            type="file"
                                            id="consent-pdf-upload"
                                            accept=".pdf"
                                            onChange={(e) => setPdfFile(e.target.files[0])}
                                            style={{ display: 'none' }}
                                        />

                                        {pdfFile ? (
                                            <div className="pdf-selected">
                                                <File size={20} />
                                                <span>{pdfFile.name}</span>
                                                <button
                                                    type="button"
                                                    className="btn-remove-file"
                                                    onClick={() => setPdfFile(null)}
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <label htmlFor="consent-pdf-upload" className="pdf-upload-label">
                                                <Upload size={24} />
                                                <span className="upload-text">Seleccionar archivo PDF</span>
                                                <span className="upload-hint">Haz clic para buscar</span>
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-submit" disabled={submitting}>
                                    {submitting ? 'Subiendo...' : 'Guardar y Subir'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .patient-consents-history {
                    padding: 1rem 0;
                }
                .consents-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }
                .header-title {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }
                .header-title h3 {
                    margin: 0;
                    font-size: 1.25rem;
                    color: #1e293b;
                }
                
                .btn-primary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: #083677;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: all 0.2s;
                }
                .btn-primary:hover {
                    background: #062c61;
                }

                .consents-list {
                    background: white;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                }

                .consent-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 1.25rem;
                    border-bottom: 1px solid #e2e8f0;
                    transition: background 0.15s;
                }
                .consent-row:last-child {
                    border-bottom: none;
                }
                .consent-row:hover {
                    background: #f8fafc;
                }

                .consent-info {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }

                .consent-name {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.95rem;
                }
                .icon-doc {
                    color: #083677;
                }

                .consent-meta {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    font-size: 0.8rem;
                    color: #64748b;
                    margin-left: 1.6rem;
                }
                .consent-meta .date {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }
                .notes-preview {
                    color: #94a3b8;
                    font-style: italic;
                }

                .consent-actions {
                    display: flex;
                    gap: 0.5rem;
                }

                .btn-icon {
                    padding: 0.5rem;
                    background: #f1f5f9;
                    border: none;
                    border-radius: 8px;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .btn-icon:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }
                .btn-icon.print {
                    color: #083677;
                    background: #eff6ff;
                }
                .btn-icon.view {
                    color: #0ea5e9;
                    background: #f0f9ff;
                }
                .btn-icon.view:hover {
                    background: #e0f2fe;
                    color: #0284c7;
                }
                .btn-icon.print:hover {
                    background: #dbeafe;
                    color: #062c61;
                }
                .btn-icon.delete:hover {
                    background: #fee2e2;
                    color: #dc2626;
                }

                .empty-state {
                    padding: 3rem;
                    text-align: center;
                    color: #64748b;
                }
                .empty-state svg {
                    color: #cbd5e1;
                    margin-bottom: 1rem;
                }
                .empty-state button {
                    margin-top: 1rem;
                    padding: 0.5rem 1rem;
                    background: #083677;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                }

                /* Modal Specifics */
                .consent-upload-modal {
                    max-width: 500px;
                    width: 90%;
                }
                .modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }
                .modal-content {
                    background: white;
                    border-radius: 12px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.25rem;
                    border-bottom: 1px solid #e2e8f0;
                }
                .modal-header h2 {
                    margin: 0;
                    font-size: 1.1rem;
                    color: #1e293b;
                }
                .close-btn {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 0.25rem;
                }
                .close-btn:hover { color: #ef4444; }

                .modal-body {
                    padding: 1.25rem;
                }

                .form-group {
                    margin-bottom: 1.25rem;
                }
                .form-group label {
                    display: block;
                    margin-bottom: 0.5rem;
                    font-size: 0.9rem;
                    font-weight: 500;
                    color: #475569;
                }
                .form-input, .form-textarea {
                    width: 100%;
                    padding: 0.75rem;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    font-family: inherit;
                }
                .form-input:focus, .form-textarea:focus {
                    outline: none;
                    border-color: #083677;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .pdf-upload-label {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 2rem;
                    border: 2px dashed #cbd5e1;
                    border-radius: 8px;
                    background: #f8fafc;
                    cursor: pointer;
                    text-align: center;
                    transition: all 0.2s;
                }
                .pdf-upload-label:hover {
                    border-color: #083677;
                    background: #eff6ff;
                }
                .pdf-upload-label svg { color: #94a3b8; }
                .pdf-upload-label:hover svg { color: #083677; }
                
                .upload-text { font-size: 0.9rem; font-weight: 500; color: #475569; }
                .upload-hint { font-size: 0.8rem; color: #94a3b8; }

                .pdf-selected {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem;
                    background: #eff6ff;
                    border: 1px solid #bfdbfe;
                    border-radius: 8px;
                }
                .pdf-selected svg { color: #083677; }
                .pdf-selected span {
                    flex: 1;
                    font-size: 0.9rem;
                    color: #1e40af;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .btn-remove-file {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 0.25rem;
                    display: flex;
                }
                .btn-remove-file:hover { color: #dc2626; }

                .modal-footer {
                    padding: 1.25rem;
                    background: #f8fafc;
                    border-top: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.75rem;
                }
                .btn-cancel {
                    padding: 0.6rem 1rem;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    color: #64748b;
                    font-weight: 500;
                    cursor: pointer;
                }
                .btn-cancel:hover { background: #f1f5f9; }
                
                .btn-submit {
                    padding: 0.6rem 1rem;
                    background: #083677;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-weight: 500;
                    cursor: pointer;
                }
                .btn-submit:hover { background: #062c61; }
                .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
            `}</style>
        </div>
    );
};

export default PatientConsentsHistory;
